/* One guitar string drawn to scale from the nut to the bridge. Each fret shortens the vibrating length by
   the same factor (2^(-1/12)), so the frets get closer towards the body and the 12th sits at the middle. */
import { useState } from 'react';
import { fretMidi } from '../core/theory';

const MARKED = [3, 5, 7, 9, 12, 15, 17, 19];

/** Distance of fret `n` from the nut, as a fraction of the scale length. */
export const fretPos = (n: number): number => 1 - 2 ** (-n / 12);

export function NeckScale({
  frets = 12,
  string = 6,
  onPick,
}: {
  frets?: number;
  /** string 1..6 whose notes the frets play */
  string?: number;
  onPick?: (fret: number, midi: number) => void;
}) {
  const [lit, setLit] = useState<number | null>(null);
  const W = 680;
  const left = 34;
  const L = W - left - 20;
  const x = (n: number) => left + L * fretPos(n);
  const cx = (n: number) => (n === 0 ? left - 16 : (x(n - 1) + x(n)) / 2);
  const yS = 46;
  const top = 30;
  const h = 32;
  const bridge = left + L;
  const pick = (n: number) => {
    onPick?.(n, fretMidi(string, n));
    setLit(n);
    setTimeout(() => setLit((v) => (v === n ? null : v)), 450);
  };
  const ids = Array.from({ length: frets + 1 }, (_, n) => n);
  return (
    <div className="fb-wrap">
      <svg
        viewBox={`0 0 ${W} 100`}
        width={W}
        height={100}
        className="neck-scale pickable"
        role="img"
        aria-label={`Ölçekli gitar sapı: ${frets} perde, 12. perde telin ortasında`}
      >
        <rect x={left} y={top} width={x(frets) - left} height={h} rx={3} className="fb-wood" />
        <rect x={x(frets)} y={top} width={bridge - x(frets)} height={h} rx={3} className="ns-body" />
        {ids.map((n) => (
          <rect
            key={`f${n}`}
            x={x(n) - (n === 0 ? 3 : 1.2)}
            y={top}
            width={n === 0 ? 6 : 2.4}
            height={h}
            className={n === 0 ? 'fb-nut' : n === 12 ? 'fb-fret ns-twelve' : 'fb-fret'}
          />
        ))}
        <rect x={bridge - 4} y={top - 4} width={8} height={h + 8} rx={2} className="ns-bridge" />
        <line x1={left - 30} x2={bridge} y1={yS} y2={yS} className="fb-string" strokeWidth={2.4} />
        {frets >= 12 ? (
          <>
            <line x1={x(12)} x2={x(12)} y1={top - 12} y2={top + h + 4} className="ns-mid" />
            <text x={x(12)} y={top - 16} className="ns-label ns-label-mid">
              12. perde: telin tam ortası
            </text>
          </>
        ) : null}
        <text x={left} y={top - 16} className="ns-label" textAnchor="start">
          Üst eşik
        </text>
        <text x={bridge} y={top - 16} className="ns-label" textAnchor="end">
          Köprü
        </text>
        {ids
          // past the 12th fret the spaces get too narrow for every number
          .filter((n) => n <= 12 || n % 2 === 1)
          .map((n) => (
            <text key={`n${n}`} x={cx(n)} y={96} className={`fb-num${MARKED.includes(n) ? ' strong' : ''}`}>
              {n}
            </text>
          ))}
        {lit != null ? <circle cx={cx(lit)} cy={yS} r={8} className="ns-lit" /> : null}
        {ids.map((n) => (
          <rect
            key={`h${n}`}
            x={n === 0 ? 0 : x(n - 1)}
            y={top - 6}
            width={n === 0 ? left - 2 : x(n) - x(n - 1)}
            height={h + 12}
            className="fb-hit"
            data-f={n}
            onPointerDown={(e) => {
              e.preventDefault();
              pick(n);
            }}
          />
        ))}
      </svg>
    </div>
  );
}
